import * as React from 'react';
import clsx from 'clsx';
import { animated, useSpring } from '@react-spring/web';
import { styled, alpha } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import ArticleIcon from '@mui/icons-material/Article';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderRounded from '@mui/icons-material/FolderRounded';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { unstable_useTreeItem2 as useTreeItem2 } from '@mui/x-tree-view/useTreeItem2';
import {
    TreeItem2Checkbox,
    TreeItem2Content,
    TreeItem2IconContainer,
    TreeItem2Label,
    TreeItem2Root,
} from '@mui/x-tree-view/TreeItem2';
import { TreeItem2Icon } from '@mui/x-tree-view/TreeItem2Icon';
import { TreeItem2Provider } from '@mui/x-tree-view/TreeItem2Provider';
import { TreeItem2DragAndDropOverlay } from '@mui/x-tree-view/TreeItem2DragAndDropOverlay';

import { formatString } from './valueFunctions';
import { getIconFromDriverType, getIconFromDriverName } from './iconFunctions';


const StyledTreeItemRoot = styled(TreeItem2Root)(({ theme }) => ({
    color:
        theme.palette.mode === 'light'
            ? theme.palette.grey[800]
            : theme.palette.grey[400],
    position: 'relative',
    [`& .${treeItemClasses.groupTransition}`]: {
        marginLeft: theme.spacing(3.5),
    },
}));

const CustomTreeItemContent = styled(TreeItem2Content)(({ theme }) => ({
    flexDirection: 'row-reverse',
    borderRadius: theme.spacing(0.7),
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    paddingRight: theme.spacing(1),
    fontWeight: 500,
    [`&.Mui-expanded `]: {
        '&:not(.Mui-focused, .Mui-selected, .Mui-selected.Mui-focused) .labelIcon': {
            color:
                theme.palette.mode === 'light'
                    ? theme.palette.primary.main
                    : theme.palette.primary.dark,
        },
        '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            left: '16px',
            top: '44px',
            height: 'calc(100% - 48px)',
            width: '1.5px',
            backgroundColor:
                theme.palette.mode === 'light'
                    ? theme.palette.grey[300]
                    : theme.palette.grey[700],
        },
    },
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.mode === 'light' ? theme.palette.primary.main : 'white',
    },
    [`&.Mui-focused, &.Mui-selected, &.Mui-selected.Mui-focused`]: {
        backgroundColor:
            theme.palette.mode === 'light'
                ? theme.palette.primary.main
                : theme.palette.primary.dark,
        color: theme.palette.primary.contrastText,
    },
}));

const AnimatedCollapse = animated(Collapse);

function TransitionComponent(props) {
    const style = useSpring({
        to: {
            opacity: props.in ? 1 : 0,
            transform: `translate3d(0,${props.in ? 0 : 20}px,0)`,
        },
    });

    return <AnimatedCollapse style={style} {...props} />;
}

const StyledTreeItemLabelText = styled(Typography)({
    color: 'inherit',
    //   fontFamily: 'General Sans',
    fontWeight: 400,
});

function CustomLabel({ icon: Icon, labelInfo, expandable, children, ...other }) {
    return (
        <TreeItem2Label
            {...other}
            sx={{
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {Icon && (
                        <Box
                            component={Icon}
                            className="labelIcon"
                            color="inherit"
                            sx={{ mr: 1, fontSize: '1.2rem' }}
                        />
                    )}

                    <StyledTreeItemLabelText variant="body2">{children}</StyledTreeItemLabelText>
                </Box>
                {!expandable &&
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography variant="caption" color="inherit">
                            {labelInfo}
                        </Typography>
                    </Box>
                }
            </Box>
        </TreeItem2Label>
    );
}

const isExpandable = (reactChildren) => {
    if (Array.isArray(reactChildren)) {
        return reactChildren.length > 0 && reactChildren.some(isExpandable);
    }
    return Boolean(reactChildren);
};

const getIconFromFileType = (fileType) => {
    switch (fileType) {
        case 'image':
            return ImageIcon;
        case 'pdf':
            return PictureAsPdfIcon;
        case 'doc':
            return ArticleIcon;
        case 'video':
            return VideoCameraBackIcon;
        case 'folder':
            return FolderRounded;
        case 'pinned':
            return FolderOpenIcon;
        case 'trash':
            return DeleteIcon;
        default:
            return ArticleIcon;
    }
};

const CustomTreeItem = React.forwardRef(function CustomTreeItem(props, ref) {
    const { id, itemId, label, disabled, children, ...other } = props;

    const {
        getRootProps,
        getContentProps,
        getIconContainerProps,
        getCheckboxProps,
        getLabelProps,
        getGroupTransitionProps,
        getDragAndDropOverlayProps,
        status,
        publicAPI,
    } = useTreeItem2({ id, itemId, children, label, disabled, rootRef: ref });

    const item = publicAPI.getItem(itemId);
    const expandable = isExpandable(children);
    let icon;
    if (item.fileType) {
        icon = getIconFromFileType(item.fileType);
    } else if (item.driverName) {
        icon = getIconFromDriverName(item.driverName);
    } else if (item.driverType) {
        icon = getIconFromDriverType(item.driverType);
    } else {
        icon = FolderRounded;
    }

    let labelInfo
    if (item.labelInfo) {
        labelInfo = item.labelInfo
    }

    return (
        <TreeItem2Provider itemId={itemId}>
            <StyledTreeItemRoot {...getRootProps(other)}>
                <CustomTreeItemContent
                    {...getContentProps({
                        className: clsx('content', {
                            'Mui-expanded': status.expanded,
                            'Mui-selected': status.selected,
                            'Mui-focused': status.focused,
                            'Mui-disabled': status.disabled,
                        }),
                    })}
                >
                    <TreeItem2IconContainer {...getIconContainerProps()}>
                        <TreeItem2Icon status={status} />
                    </TreeItem2IconContainer>
                    <TreeItem2Checkbox {...getCheckboxProps()} />
                    <CustomLabel
                        {...getLabelProps({ icon, labelInfo, expandable: expandable && status.expanded })}
                    />
                    <TreeItem2DragAndDropOverlay {...getDragAndDropOverlayProps()} />
                </CustomTreeItemContent>
                {children && <TransitionComponent {...getGroupTransitionProps()} />}
            </StyledTreeItemRoot>
        </TreeItem2Provider>
    );
});

// Recursive function to filter out hidden children
const filterHiddenChildren = (data) => {
    return data.map((item) => ({
        ...item,
        children: item.children
            ? filterHiddenChildren(item.children.filter((child) => !child.hidden)) // Filter and recurse
            : [],
    }));
};

export default function ValueModelTree({ costStructure, investmentStructure, productTypes, revenueTypes, selected, setSelected, nofValueDrivers }) {

    const createDriverChildren = (parentId, costStructure) => {
        const children = Object.keys(costStructure || {}).map((child, index) => {
            return { id: `${parentId}.${index + 1}`, label: formatString(child), driverName: child, labelInfo: nofValueDrivers['cost_value_driver'][child] }
        })
        return children
    }

    const handleNodeSelect = (event, nodeId) => {
        // Define the node IDs that should not be selectable
        const nonSelectableNodes = ['1', '1.1', '1.2', '1.3', '2'];

        // Check if the selected node ID is in the list of non-selectable nodes
        const isNonSelectable = nonSelectableNodes.includes(nodeId);

        // If the node is selectable, update the selected state
        if (!isNonSelectable) {
            setSelected([nodeId]);
        }
    };
    
    const DRIVERS = [
        {
            id: '1',
            label: 'Cost Value Drivers',
            driverType: 'cost',
            children: [
                {
                    id: '1.1',
                    label: 'Operating costs',
                    driverType: 'operatingCost',
                    children: createDriverChildren('1.1', costStructure)
                },
                {
                    id: '1.2',
                    label: 'Operating capital',
                    driverType: 'operatingCapital',
                    children: [
                        {
                            id: '1.2.1',
                            label: 'Inventory',
                            driverName: 'inventory_costs',
                            labelInfo: nofValueDrivers['cost_value_driver']['inventory_costs'],
                            hidden: Object.keys(costStructure?.['operational_costs'] || {}).includes('warehouse_storage') ? false : true
                        },
                        {
                            id: '1.2.2',
                            label: 'Faster payment',
                            driverName: 'faster_payment',
                            labelInfo: nofValueDrivers['cost_value_driver']['faster_payment']
                        },
                    ]
                },
                {
                    id: '1.3',
                    label: 'Capital investment',
                    driverType: 'capitalInvestment',
                    children: createDriverChildren('1.3', investmentStructure),
                    hidden: investmentStructure ? false : true,
                },
            ],
        },
        {
            id: '2',
            label: 'Revenue Value Drivers',
            driverType: 'revenue',
            children: [
                {
                    id: '2.1',
                    label: 'Sales-driven revenue',
                    driverType: 'sales',
                    labelInfo: nofValueDrivers['revenue_value_driver']['sales-driven_revenue']
                },
                {
                    id: '2.2',
                    label: 'Marketing-driven revenue',
                    driverType: 'marketing',
                    labelInfo: nofValueDrivers['revenue_value_driver']['marketing-driven_revenue']
                },
                {
                    id: '2.3',
                    label: 'Time to market',
                    driverType: 'ttm',
                    labelInfo: nofValueDrivers['revenue_value_driver']['time_to_market'],
                    hidden: productTypes ? false : true,
                },
                {
                    id: '2.4',
                    label: 'Incremental revenue',
                    driverType: 'npm',
                    labelInfo: nofValueDrivers['revenue_value_driver']['incremental_revenue'],
                    hidden: revenueTypes ? false : true,
                },
                {
                    id: '2.5',
                    label: 'Customer lifetime value',
                    driverType: 'clv',
                    labelInfo: nofValueDrivers['revenue_value_driver']['customer_lifetime_value']
                },
            ]
        },
        {
            id: '3',
            label: 'Risk Value Drivers',
            driverType: 'risk',
            children: [
                {
                    id: '3.1',
                    label: 'Regulatory fines/penalties',
                    driverType: 'regulatory',
                    labelInfo: nofValueDrivers['risk_value_driver']['regulatory_risk']
                },
                {
                    id: '3.2',
                    label: 'Litigation risks',
                    driverType: 'litigation',
                    labelInfo: nofValueDrivers['risk_value_driver']['litigation_risk']
                },
                {
                    id: '3.3',
                    label: 'Revenue loss',
                    driverType: 'revenueLoss',
                    labelInfo: nofValueDrivers['risk_value_driver']['revenue_loss'],
                },
            ]
        },        
    ];

    return (
        <RichTreeView
            items={filterHiddenChildren(DRIVERS)}
            defaultExpandedItems={['1', '1.1', '2', '3']}
            // defaultSelectedItems={'1.1.1'}
            selectedItems={selected}
            onSelectedItemsChange={handleNodeSelect}
            sx={{ height: 'fit-content', flexGrow: 1, maxWidth: 400, overflowY: 'auto' }}
            slots={{ item: CustomTreeItem }}
        />
    );
}